import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { decode } from 'base-64';
import Header from '../../component/loaderComponent/Header/Header';
import NoDataFound from '../../component/noDataComponent/NoDataFound';
import { notify } from '../../services/notification';
import { getSingleCustomerDetailAction } from '../../actions/targetAchievementAction';
import './singleCustomerDetail.scss';
import { formatMonth, isCurrentMonth} from '../../constants/dateTimeUtils/dateTimeUtils';

export default function SingleCustomerDetail() {
  const {
    customerCode: encodedCustomerCode,
    customerName: encodedCustomerName,
    apsCode: encodedApsCode,
    month: encodedMonth
  } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Decode URL parameters
  const customerCode = decode(encodedCustomerCode);
  const customerName = decode(encodedCustomerName);
  const apsCode = decode(encodedApsCode);
  const month = decode(encodedMonth);

  // State
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomerDetail();
  }, [customerCode]);

  const fetchCustomerDetail = async () => {
    try {
      setLoading(true);

      const response = await dispatch(getSingleCustomerDetailAction(customerCode));

      if (response?.status === 200) {
        const apiData = response.data.data || [];
        setDataList(Array.isArray(apiData) ? apiData : []);
      }
    } catch (error) {
      notify.error("Failed to load customer detail");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="single-customer-detail-wrapper">
      <Header title="Plan vs Actual" />

      <div className="single-customer-detail-content">
        {/* Header Section */}
        <div className="single-customer-detail-header">
          <div className="header-info">
            <h2>Customer Detail: {customerName}</h2>
            <p className="subtitle">
              Customer Code: {customerCode} | APS: {apsCode} | Month: {formatMonth(month)}
            </p>
          </div>
          <button className="close-btn" onClick={handleBack}>
            Close
          </button>
        </div>

        {/* Table Section */}
        <section className="table-section">
          {loading ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>SNo</th>
                    <th>Month</th>
                    <th>Monthly Plan</th>
                    <th>Monthly Actual</th>
                    <th>Monthly Achievement %</th>
                    <th>YTD Plan</th>
                    <th>YTD Actual</th>
                    <th>YTD Achievement %</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="skeleton-row">
                      <td><span className="skeleton text w-10" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-20" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : dataList.length === 0 ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>SNo</th>
                    <th>Month</th>
                    <th>Monthly Plan</th>
                    <th>Monthly Actual</th>
                    <th>Monthly Achievement %</th>
                    <th>YTD Plan</th>
                    <th>YTD Actual</th>
                    <th>YTD Achievement %</th>
                  </tr>
                </thead>
              </table>
              <div className="no-data-wrapper">
                <NoDataFound />
              </div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>SNo</th>
                    <th>Month</th>
                    <th>Monthly Plan</th>
                    <th>Monthly Actual</th>
                    <th>Monthly Achievement %</th>
                    <th>YTD Plan</th>
                    <th>YTD Actual</th>
                    <th>YTD Achievement %</th>
                  </tr>
                </thead>
                <tbody>
                  {dataList.map((row, index) => (
                    <tr key={index} className={isCurrentMonth(row.month) ? 'current-month-row' : ''}>
                      <td className="sno-cell">{index + 1}</td>
                      <td className="month-cell">{formatMonth(row.month)}</td>
                      <td className="plan-cell">
                        {row.monthly_plan || row.monthly_plan === 0
                          ? Number(row.monthly_plan).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : 'N/A'}
                      </td>
                      <td className="actual-cell">
                        {row.monthly_actual || row.monthly_actual === 0
                          ? Number(row.monthly_actual).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="achievement-cell">
                        {row.monthly_pct || row.monthly_pct === 0
                          ? `${Number(row.monthly_pct).toFixed(2)}%`
                          : 'N/A'}
                      </td>
                      <td className="ytd-plan-cell">
                        {row.ytd_plan || row.ytd_plan === 0
                          ? Number(row.ytd_plan).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : 'N/A'}
                      </td>
                      <td className="ytd-actual-cell">
                        {row.ytd_actual || row.ytd_actual === 0
                          ? Number(row.ytd_actual).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="ytd-achievement-cell">
                        {row.ytd_pct || row.ytd_pct === 0
                          ? `${Number(row.ytd_pct).toFixed(2)}%`
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
